import { defineStore } from 'pinia'
import api from '../api/client'
import { useAuthStore } from './auth'

function normalizeCodes(raw: unknown): string[] {
  if (!Array.isArray(raw)) return []
  return Array.from(
    new Set(
      raw
        .map((code) => String(code || '').trim().toLowerCase())
        .filter((code) => !!code),
    ),
  )
}

export const usePermissionGuardStore = defineStore('permission-guard', {
  state: () => ({
    loaded: false,
    loading: false,
    error: null as string | null,
    permissionCodes: [] as string[],
    permissionsByApplication: {} as Record<string, string[]>,
    isSuperAdmin: false,
  }),

  getters: {
    hasPermission: (state) => {
      return (code: string) => {
        if (state.isSuperAdmin) return true
        const target = String(code || '').trim().toLowerCase()
        if (!target) return false
        return state.permissionCodes.includes(target)
      }
    },
    hasAnyPermission: (state) => {
      return (codes: string[]) => {
        if (state.isSuperAdmin) return true
        if (!Array.isArray(codes) || !codes.length) return true
        return codes.some((code) => {
          const target = String(code || '').trim().toLowerCase()
          return !!target && state.permissionCodes.includes(target)
        })
      }
    },
  },

  actions: {
    syncFromAuth() {
      const auth = useAuthStore()
      this.permissionCodes = normalizeCodes(auth.permissionCodes)
      this.isSuperAdmin = Boolean(auth.user?.is_super_admin)
    },

    async loadResolvedPermissions(force = false) {
      const auth = useAuthStore()
      if (!auth.token) {
        this.reset()
        return
      }
      if (this.loaded && !force) return

      this.loading = true
      this.error = null
      try {
        const { data } = await api.get('/api/core/auth/me/permissions/')
        const normalized = normalizeCodes(data?.permission_codes || data?.permissions)
        this.permissionCodes = normalized
        this.permissionsByApplication = (data?.permissions_by_application || {}) as Record<string, string[]>
        this.isSuperAdmin = Boolean(data?.is_super_admin || auth.user?.is_super_admin)
        this.loaded = true

        auth.permissionCodes = normalized
        auth.persist()
      } catch (error: any) {
        this.syncFromAuth()
        this.error =
          error?.response?.data?.detail ||
          error?.message ||
          'Unable to load permissions.'
        this.loaded = true
      } finally {
        this.loading = false
      }
    },

    reset() {
      this.loaded = false
      this.loading = false
      this.error = null
      this.permissionCodes = []
      this.permissionsByApplication = {}
      this.isSuperAdmin = false
    },
  },
})
